/**
 * Manage Schools Dialog
 * Simple org-scoped list of school names a director maintains once, to avoid
 * typos. Feeds the School Name dropdown in Edit Child (School Age / After
 * School) so every child's school is a consistent, exact value.
 *
 * Add/Delete is restricted to Director, Admin, or Business Manager
 * (canManageSchools) — other staff can still view the list read-only.
 */
import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Trash2, GraduationCap, Loader2, AlertCircle } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const authHeaders = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
});

export const ManageSchoolsDialog = ({ open, onOpenChange, onSchoolsChanged, canEdit = false }) => {
  const [schools, setSchools] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [newName, setNewName] = useState("");
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(false);
    try {
      const res = await axios.get(`${API}/schools`, authHeaders());
      setSchools(res.data || []);
    } catch (err) {
      setLoadError(true);
      toast.error(err.response?.data?.detail || "Could not load schools");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open) load();
  }, [open, load]);

  const handleAdd = async () => {
    const name = newName.trim();
    if (!name) return;
    setSaving(true);
    try {
      await axios.post(`${API}/schools`, { name }, authHeaders());
      setNewName("");
      await load();
      onSchoolsChanged?.();
      toast.success("School added");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not add school");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (schoolId) => {
    try {
      await axios.delete(`${API}/schools/${schoolId}`, authHeaders());
      await load();
      onSchoolsChanged?.();
      toast.success("School removed");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not remove school");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="manage-schools-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <GraduationCap className="w-5 h-5 text-blue-600" />
            Manage Schools
          </DialogTitle>
          <DialogDescription>
            Keep one consistent list of school names — this feeds the School Name dropdown on
            School Age and After School children, so every record matches exactly.
          </DialogDescription>
        </DialogHeader>

        {canEdit ? (
          <div className="flex gap-2">
            <Input
              placeholder="e.g. Baden Academy"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleAdd()}
              data-testid="manage-schools-new-input"
            />
            <Button onClick={handleAdd} disabled={saving || !newName.trim()} data-testid="manage-schools-add-btn">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Add"}
            </Button>
          </div>
        ) : (
          <p className="text-xs text-slate-500 bg-slate-50 border border-slate-200 rounded-md px-3 py-2" data-testid="manage-schools-readonly-note">
            Only Director, Admin, or Business Manager can add or remove schools. You can select an
            existing school on a child's record.
          </p>
        )}

        <div className="space-y-1 max-h-64 overflow-y-auto">
          {loading ? (
            <p className="text-sm text-gray-400 py-2" data-testid="manage-schools-loading">Loading...</p>
          ) : loadError ? (
            <div className="flex items-center justify-between gap-2 text-sm text-red-600 bg-red-50 border border-red-200 rounded-md px-3 py-2" data-testid="manage-schools-load-error">
              <span className="flex items-center gap-1.5"><AlertCircle className="w-4 h-4" /> Could not load schools</span>
              <Button size="sm" variant="outline" onClick={load} data-testid="manage-schools-retry-btn">Retry</Button>
            </div>
          ) : schools.length === 0 ? (
            <p className="text-sm text-gray-400 py-2" data-testid="manage-schools-empty">No schools added yet.</p>
          ) : (
            schools.map((s) => (
              <div
                key={s.id}
                className="flex items-center justify-between px-3 py-2 rounded-md bg-slate-50 border border-slate-200"
                data-testid={`manage-schools-row-${s.id}`}
              >
                <span className="text-sm text-slate-700">{s.name}</span>
                {canEdit && (
                  <button
                    onClick={() => handleDelete(s.id)}
                    className="text-slate-400 hover:text-red-500"
                    data-testid={`manage-schools-delete-${s.id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
              </div>
            ))
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
